import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Typography, Container, IconButton, Drawer } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLenis } from '@studio-freight/react-lenis';
import { images } from '../assets/images';
import { gradients } from '../theme';

const navItems = [
  { label: 'Home', id: 'home' },
  { label: 'About Us', id: 'about' },
  { label: 'Services', id: 'services' },
  { label: 'Contact Us', id: 'contact' },
];

const Navigation: React.FC = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const lenis = useLenis(({ scroll }) => {
    // Track scroll for navbar background
    setScrolled(scroll > 50);
  });

  const handleNavClick = (id: string) => {
    setMobileOpen(false); // Close mobile menu

    if (lenis) {
      lenis.scrollTo(`#${id}`, {
        offset: -80,
        duration: 1.2,
      });
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(20px)',
          height: 70,
          boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none',
          borderBottom: `1px solid ${scrolled ? 'rgba(0, 242, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'}`,
          zIndex: 1100,
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '70px !important',
              px: 0,
            }}
          >
            {/* Logo */}
            <Box
              component={motion.div}
              whileHover={{ scale: 1.05 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Box
                component="img"
                src={images.logo}
                alt="Bluecat Logo"
                sx={{
                  height: 50,
                  width: 'auto',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
                onClick={() => handleNavClick('home')}
              />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 5 }}>
              {navItems.map((item) => (
                <Box
                  key={item.label}
                  component={motion.div}
                  whileHover={{ y: -2 }}
                  onClick={() => handleNavClick(item.id)}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    padding: '8px 0',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      background: activeSection === item.id ? gradients.primary : 'none',
                      WebkitBackgroundClip: activeSection === item.id ? 'text' : 'none',
                      WebkitTextFillColor: activeSection === item.id ? 'transparent' : 'white',
                      color: activeSection === item.id ? 'transparent' : 'white',
                      '&:hover': {
                        color: '#00f2ff',
                      },
                    }}
                  >
                    {item.label}
                  </Typography>
                  {activeSection === item.id && (
                    <Box
                      component={motion.span}
                      layoutId="activeIndicator"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        background: gradients.primary,
                        borderRadius: '3px',
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 242, 255, 0.1)',
                },
              }}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={28} /> : <Menu size={28} />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(0, 242, 255, 0.2)',
            pt: 9,
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {navItems.map((item, index) => (
            <Box
              key={item.label}
              component={motion.div}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleNavClick(item.id)}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                p: 2,
                borderRadius: 2,
                backgroundColor: activeSection === item.id
                  ? 'rgba(0, 242, 255, 0.1)'
                  : 'transparent',
                border: `1px solid ${activeSection === item.id
                  ? 'rgba(0, 242, 255, 0.3)'
                  : 'transparent'
                  }`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 242, 255, 0.05)',
                  borderColor: 'rgba(0, 242, 255, 0.2)',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  background: activeSection === item.id ? gradients.primary : 'none',
                  WebkitBackgroundClip: activeSection === item.id ? 'text' : 'none',
                  WebkitTextFillColor: activeSection === item.id ? 'transparent' : 'white',
                  color: activeSection === item.id ? 'transparent' : 'white',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
